// ─────────────────────────────────────────────────────────────────────────────
// Step-completion celebration — a floating graphic card between tasks
// 2→3, 3→4, ... 7→8.
//
// UX flow:
//   1. User clicks Continue on a task → this card floats in over the content
//   2. A warm amber orb travels along the step bar from task N to task N+1
//      over 2.8s (easeOut) so the movement is clearly perceptible
//   3. Big "N → N+1" transition label above the bar makes the progress
//      unmistakable ("Moving from Task 4 to Task 5")
//   4. Two CTAs inside the card: "Continue" or "Pause for now"
//   5. Pause → warmer congrats screen → routes to /dashboard (guests → /)
//
// This is a FLOATING CARD, not a full-screen modal — the practice content
// stays visible behind a very soft dimmer so the user sees the context they
// just completed. The card itself has a scale-in entrance so it reads as
// "arriving" rather than "blocking".
//
// Auth-agnostic: works for guests too (Dial Down Tension first-4-tasks flow).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Pause, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

// Total animation time for the orb to travel one step (dot → next dot).
// Set to 1500ms per the product spec — slow enough to comfortably track
// the ball, snappy enough to feel like real forward motion.
const ORB_TRAVEL_MS = 1500;
const ORB_TRANSITION = `left ${ORB_TRAVEL_MS}ms linear`;
const FILL_TRANSITION = `width ${ORB_TRAVEL_MS}ms linear`;
const ARROW_TRANSITION = `transform ${ORB_TRAVEL_MS}ms linear`;

export const StepCompletionCelebration = ({
  currentTaskNumber,      // number the user just completed
  nextTaskTitle,          // "Second Pass Through the Story" etc.
  totalTasks = 9,         // total steps in this practice
  onContinue,             // called when they hit Continue
  onPause,                // called when they hit Pause → parent routes away
  isGuest = false,        // guests see the same overlay, different route on Pause
}) => {
  const [animated, setAnimated] = useState(false);
  const [entered, setEntered] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    // Two-stage kickoff so the CSS transition definitely has a
    // starting frame to interpolate from. We paint once with the orb
    // at `startPct`, then after 60ms swap it to `endPct` — that
    // guaranteed paint gap prevents React 18 batching from collapsing
    // both state updates into a single render (which would let the
    // orb jump straight to the target with no visible travel).
    const rafId = requestAnimationFrame(() => setEntered(true));
    const timeoutId = setTimeout(() => setAnimated(true), 60);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, []);

  // Position along the tracker as % — from currentTaskNumber to next.
  const startPct = ((currentTaskNumber - 1) / (totalTasks - 1)) * 100;
  const endPct = (currentTaskNumber / (totalTasks - 1)) * 100;
  const ballLeft = animated ? endPct : startPct;
  const nextTaskNumber = currentTaskNumber + 1;

  // ── Pause state: warmer congrats screen. ──────────────────────────────────
  if (paused) {
    return (
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center bg-[#1B2349]/85 backdrop-blur-sm px-4 py-8 overflow-y-auto"
        data-testid="step-pause-congrats"
      >
        <div className="relative w-full max-w-md bg-[#FBF8F1] rounded-3xl shadow-2xl p-8 sm:p-10 my-auto">
          <div className="flex justify-center mb-5">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F6C071] via-[#E29B4C] to-[#D97706] flex items-center justify-center shadow-[0_0_40px_rgba(230,155,76,0.55)]">
              <Sparkles className="w-9 h-9 text-white" />
            </div>
          </div>
          <h2
            className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] text-center mb-4"
            data-testid="step-pause-title"
          >
            Beautiful work.
          </h2>
          <p className="text-[#4A5568] text-base leading-relaxed text-center mb-8">
            I'm looking forward to seeing you soon — many people need a day or two rest between steps, and that's exactly right. Your progress is safe, and it'll be here waiting for you.
          </p>
          <Button
            onClick={onPause}
            className="w-full rounded-full bg-[#1B2349] text-white hover:bg-[#0F1838] h-12 text-base font-medium"
            data-testid="step-pause-close-btn"
          >
            {isGuest ? "Back to home" : "See you soon"}
          </Button>
        </div>
      </div>
    );
  }

  // ── Celebration state: floating card + orb animation + two CTAs. ─────────
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1B2349]/25 px-4 py-6 overflow-y-auto pointer-events-auto"
      data-testid="step-completion-celebration"
    >
      {/* Floating card — scales in from 96% + fade so it reads as arriving,
          not blocking. Softer shadow than a modal for the "floating" feel. */}
      <div
        className={`relative w-full max-w-xl bg-[#FBF8F1] rounded-2xl shadow-[0_20px_60px_-15px_rgba(27,35,73,0.45)] ring-1 ring-black/5 p-6 sm:p-8 my-auto transition-all duration-500 ease-out ${
          entered ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-[0.96] translate-y-2"
        }`}
      >
        {/* Task-transition badge — the "4 → 5" hero moment. */}
        <div className="flex items-center justify-center gap-3 mb-1">
          <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#E29B4C] font-bold">
            <span>Task {currentTaskNumber}</span>
            <span className="text-[#1B2349]/40">·</span>
            <span>Complete</span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mb-5" data-testid="step-transition-label">
          <span className="font-outfit font-semibold text-2xl text-[#1B2349]/40 tabular-nums">
            {currentTaskNumber}
          </span>
          <ArrowRight
            className="w-5 h-5 text-[#E29B4C]"
            style={{
              transition: ARROW_TRANSITION,
              transform: animated ? "translateX(4px)" : "translateX(0)",
            }}
          />
          <span className="font-outfit font-semibold text-2xl text-[#1B2349] tabular-nums">
            {nextTaskNumber}
          </span>
        </div>

        <h2
          className="font-outfit font-semibold text-lg sm:text-xl text-[#1B2349] text-center mb-5"
          data-testid="step-celebration-title"
        >
          Nicely done — you moved through it.
        </h2>

        {/* ── Step tracker with animated glowing amber orb ─────────────── */}
        <div className="relative mx-auto mb-5 h-14" data-testid="step-celebration-tracker">
          {/* Baseline track */}
          <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 h-1 bg-[#1B2349]/10 rounded-full" />
          {/* Filled portion up to the next task — constant-speed linear
              motion so the ball is equally visible from start to finish.
              Inline `transition` beats Tailwind's transition class
              ordering, which can otherwise leak a 150ms default. */}
          <div
            className="absolute left-3 top-1/2 -translate-y-1/2 h-1 rounded-full bg-gradient-to-r from-[#F6C071] to-[#E29B4C]"
            style={{
              width: `calc((100% - 24px) * ${ballLeft / 100})`,
              transition: FILL_TRANSITION,
            }}
          />
          {/* Step dots */}
          {Array.from({ length: totalTasks }).map((_, i) => {
            const stepNum = i + 1;
            const isFilled = stepNum <= currentTaskNumber || (animated && stepNum === nextTaskNumber);
            const isTarget = animated && stepNum === nextTaskNumber;
            return (
              <div
                key={stepNum}
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full transition-all duration-500 ${
                  isTarget ? "w-4 h-4 ring-2 ring-[#E29B4C]/40 ring-offset-2 ring-offset-[#FBF8F1]" : "w-3 h-3"
                } ${
                  isFilled ? "bg-[#E29B4C]" : "bg-[#1B2349]/20"
                }`}
                style={{
                  left: `calc(12px + ((100% - 24px) * ${(stepNum - 1) / (totalTasks - 1)}))`,
                  transitionDelay: isTarget ? `${ORB_TRAVEL_MS - 200}ms` : "0ms",
                }}
                data-testid={stepNum === nextTaskNumber ? "step-target-dot" : undefined}
              />
            );
          })}
          {/* The traveling orb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
            style={{
              left: `calc(12px + ((100% - 24px) * ${ballLeft / 100}))`,
              transition: ORB_TRANSITION,
            }}
            data-testid="step-celebration-orb"
          >
            <div className="relative">
              {/* Outer glow */}
              <div className="absolute inset-0 -m-4 rounded-full bg-[#F6C071] blur-xl opacity-60 animate-pulse" />
              {/* Middle glow */}
              <div className="absolute inset-0 -m-2 rounded-full bg-[#E29B4C] blur-md opacity-80" />
              {/* The orb itself */}
              <div className="relative w-7 h-7 rounded-full bg-gradient-to-br from-[#FFE5B4] via-[#F6C071] to-[#D97706] shadow-[0_0_20px_rgba(230,155,76,0.9)]">
                {/* Inner highlight */}
                <div className="absolute top-1 left-1.5 w-2.5 h-2.5 rounded-full bg-white/60 blur-[1px]" />
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-[#4A5568] text-sm mb-6">
          Next up: <span className="font-semibold text-[#1B2349]">{nextTaskTitle || `Task ${nextTaskNumber}`}</span>
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => setPaused(true)}
            variant="outline"
            className="flex-1 rounded-full h-11 text-sm font-medium border-[#1B2349]/20 text-[#1B2349] hover:bg-[#F4F1E8]"
            data-testid="step-celebration-pause-btn"
          >
            <Pause className="w-4 h-4 mr-2" />
            Pause for now
          </Button>
          <Button
            onClick={onContinue}
            className="flex-1 rounded-full h-11 text-sm font-medium bg-[#1B2349] text-white hover:bg-[#0F1838]"
            data-testid="step-celebration-continue-btn"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepCompletionCelebration;
